import SafeAppsSDK, {
  TransactionStatus as GnosisTransactionStatus,
  SafeInfo,
} from "@safe-global/safe-apps-sdk";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAccount } from "wagmi";
import { areSameAddress } from "../../services/internal/walletService";
import { useSwap } from "../swap/useSwap";

export const useGnosisContext = () => {
  const { connector, isConnected, address } = useAccount();
  const [isGnosisContext, setIsGnosisContext] = useState(false);
  const [gnosisSafeInfo, setGnosisSafeInfo] = useState<SafeInfo>();

  /**
   * Method that will be used to send transaction
   * TODO: could have loaded the sdk when app load and stored globally
   */
  const getGnosisSafeContext = useCallback(async () => {
    const appsSdk = new SafeAppsSDK();
    const safe = await appsSdk.safe.getInfo();
    const isSafeContext =
      safe.chainId !== undefined &&
      safe.safeAddress !== undefined &&
      connector?.id === "safe";

    setGnosisSafeInfo(safe);
    setIsGnosisContext(isSafeContext);

    if (isSafeContext) return appsSdk;

    return undefined;
  }, [connector]);

  useEffect(() => {
    getGnosisSafeContext();
  }, [getGnosisSafeContext]);

  /**
   * There's a specific way to get the transaction hash for the safe connector
   * Using then initial hash received by the transaction, we can get the real hash
   * @param hashReceived
   * @returns
   */
  const getGnosisTransactionHash = async (
    initialHash: string
  ): Promise<string> => {
    const safeSdk = await getGnosisSafeContext();

    const tx = await safeSdk?.txs.getBySafeTxHash(initialHash);
    const status: GnosisTransactionStatus | undefined = tx?.txStatus;
    if (
      status !== GnosisTransactionStatus.FAILED &&
      status !== GnosisTransactionStatus.SUCCESS &&
      status !== GnosisTransactionStatus.CANCELLED
    ) {
      // Workaround, Wait 2 seconds before checking the gnosis status again
      // TODO: There might be a better way to do this
      // eslint-disable-next-line no-promise-executor-return
      await new Promise((res) => setTimeout(res, 2000));
      return getGnosisTransactionHash(initialHash);
    }
    return tx?.txHash ?? initialHash;
  };

  const isGnosisConnected = connector?.id === "safe" && isConnected;

  return {
    gnosisSafeInfo,
    isGnosisContext,
    getGnosisTransactionHash,
    isGnosisConnected,
    gnosisAddress: isGnosisConnected ? address : undefined,
  };
};

export const useIsSameAddressAndGnosisContext = () => {
  const { isGnosisContext, gnosisAddress: gnosisSourceAddress } =
    useGnosisContext();
  const { destinationAddress } = useSwap();

  /**
   * Gnosis Safe addresses are not always the same on all chains
   * We'll warn the user if they are trying to send to the same address
   */
  const isSameAddressAndGnosisContext = useMemo(() => {
    const destAddressSameAsSource = areSameAddress(
      gnosisSourceAddress,
      destinationAddress.address
    );

    return isGnosisContext && destAddressSameAsSource;
  }, [gnosisSourceAddress, destinationAddress.address, isGnosisContext]);

  return {
    isSameAddressAndGnosisContext,
  };
};
